import type {
	IamUser,
	IamUserListResponse,
	CreateUserPayload,
	UpdateUserPayload,
} from '../types/iam-user.types';
import type { CursorParams } from '../types/api.types';
import { api } from './client';

/**
 * Query key factory for users
 */
export const userKeys = {
	all: ['users'] as const,
	lists: () => [...userKeys.all, 'list'] as const,
	list: (params?: Record<string, unknown>) => [...userKeys.lists(), params] as const,
	details: () => [...userKeys.all, 'detail'] as const,
	detail: (id: string) => [...userKeys.details(), id] as const,
};

/**
 * Fetch list of users
 */
export async function getUsers(params?: CursorParams): Promise<IamUserListResponse> {
	const response = await api.get<IamUserListResponse>('/v1/iam/users', { params });
	return response.data;
}

/**
 * Fetch a single user by ID
 */
export async function getUser(id: string): Promise<IamUser> {
	const response = await api.get<IamUser>(`/v1/iam/users/${id}`);
	return response.data;
}

/**
 * Create a new user
 */
export async function createUser(payload: CreateUserPayload): Promise<IamUser> {
	const response = await api.post<IamUser>('/v1/iam/users', payload);
	return response.data;
}

/**
 * Update an existing user
 */
export async function updateUser(id: string, payload: UpdateUserPayload): Promise<IamUser> {
	const response = await api.put<IamUser>(`/v1/iam/users/${id}`, payload);
	return response.data;
}

/**
 * Delete a user
 */
export async function deleteUser(id: string): Promise<void> {
	await api.delete(`/v1/iam/users/${id}`);
}

/**
 * Reset a user's password
 */
export async function resetUserPassword(id: string): Promise<void> {
	await api.post(`/v1/iam/users/${id}/reset-password`);
}
